import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect, useState } from "react";
import { Upload, FileVideo, Check, Play, Volume2 } from "lucide-react";
import { cn } from "@/lib/utils";

const vslThumbOriginal = "/assets/imgs/vsl-thumb-original.jpg";

interface UploadSceneProps {
  isActive: boolean;
}

type UploadPhase = 
  | "idle"
  | "cursor-enter"
  | "cursor-click"
  | "video-appear"
  | "file-appear"
  | "uploading"
  | "complete";

const CURSOR_POSITIONS = {
  start: { x: 260, y: 280 },
  dropzone: { x: 140, y: 100 },
};

export function UploadScene({ isActive }: UploadSceneProps) {
  useLanguage();
  const [phase, setPhase] = useState<UploadPhase>("idle");
  const [progress, setProgress] = useState(90);
  const [cursorPos, setCursorPos] = useState(CURSOR_POSITIONS.start);
  const [cursorScale, setCursorScale] = useState(1);

  // Reset when inactive
  useEffect(() => {
    if (!isActive) {
      setPhase("idle");
      setProgress(90);
      setCursorPos(CURSOR_POSITIONS.start);
      setCursorScale(1);
      return;
    }

    // Start animation sequence
    const timers: NodeJS.Timeout[] = [];

    // T0 → T1: Cursor enters (300ms)
    timers.push(setTimeout(() => {
      setPhase("cursor-enter");
      setCursorPos(CURSOR_POSITIONS.dropzone);
    }, 300));

    // T1 → T2: Cursor click (1000ms)
    timers.push(setTimeout(() => {
      setPhase("cursor-click");
      setCursorScale(0.85);
    }, 1000));

    // Cursor scale back
    timers.push(setTimeout(() => {
      setCursorScale(1);
    }, 1150));

    // T2 → T3: Video appears (1200ms)
    timers.push(setTimeout(() => {
      setPhase("video-appear");
    }, 1200));

    // T3 → T4: File appears (1600ms)
    timers.push(setTimeout(() => {
      setPhase("file-appear");
    }, 1600));

    // T4 → T5: Start uploading (1800ms)
    timers.push(setTimeout(() => {
      setPhase("uploading");
    }, 1800));

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [isActive]);

  // Progress animation (90% → 100%)
  useEffect(() => {
    if (phase !== "uploading") return;

    const startProgress = 90;
    const endProgress = 100;
    const duration = 1200;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = startProgress + (endProgress - startProgress) * eased;

      setProgress(Math.round(current));

      if (current < 100) {
        requestAnimationFrame(animate);
      } else {
        setPhase("complete");
      }
    };

    requestAnimationFrame(animate);
  }, [phase]);

  const isDropzoneHighlighted = phase === "cursor-click" || phase === "cursor-enter";
  const showDropzone = phase === "idle" || phase === "cursor-enter" || phase === "cursor-click";
  const showThumb = ["video-appear", "file-appear", "uploading", "complete"].includes(phase);
  const showFile = phase === "file-appear" || phase === "uploading" || phase === "complete";
  const showProgress = phase === "uploading" || phase === "complete";
  const isComplete = phase === "complete";
  const showCursor = phase !== "idle" && phase !== "complete" && phase !== "video-appear" && phase !== "file-appear" && phase !== "uploading";

  return (
    <div className="relative flex flex-col h-full px-5 py-4">
      {/* Dropzone - esconde após click */}
      {showDropzone && (
        <div
          className={cn(
            "border-2 border-dashed rounded-xl p-5 transition-all duration-200 mb-3",
            isDropzoneHighlighted
              ? "border-[#1F6BFF] bg-[#1F6BFF]/5"
              : "border-gray-300 bg-gray-50/50"
          )}
        >
          {/* Upload Icon */}
          <div className="w-11 h-11 mx-auto mb-2.5 rounded-full bg-[#1F6BFF]/10 flex items-center justify-center">
            <Upload className="w-5 h-5 text-[#1F6BFF]" />
          </div>

          {/* Text */}
          <h4 className="text-[13px] font-semibold text-gray-800 text-center">
            {translateInterfaceText("Faça upload do seu vídeo")}</h4>
          <p className="text-[11px] text-gray-500 text-center mt-0.5">
            {translateInterfaceText("Arraste ou clique para enviar")}</p>

          {/* Format badge */}
          <div className="flex justify-center mt-2.5">
            <span className="px-2 py-0.5 bg-gray-100 rounded-full text-[9px] text-gray-400">
              {translateInterfaceText("MP4, MOV • até 5GB")}</span>
          </div>
        </div>
      )}

      {/* Video Thumb + Autoplay Modal */}
      <div
        className={cn(
          "relative mb-3 transition-all duration-300",
          showThumb ? "opacity-100 scale-100" : "opacity-0 scale-95 hidden"
        )}
      >
        {/* Thumbnail */}
        <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-900">
          <img 
            src={vslThumbOriginal} 
            alt={translateInterfaceText("Video thumbnail")}
            className="w-full h-full object-cover"
          />
          
          {/* Play button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <Play className="w-5 h-5 text-gray-800 ml-0.5" />
            </div>
          </div>
          
          {/* Autoplay Modal Overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="bg-[#E91E8C]/95 rounded-xl px-5 py-4 text-center animate-autoplay-pulse shadow-xl">
              <p className="text-white font-bold text-[13px] mb-2">{translateInterfaceText("Seu vídeo já começou")}</p>
              <Volume2 className="w-8 h-8 text-white mx-auto mb-2" />
              <p className="text-white/90 text-[11px]">{translateInterfaceText("Clique para ouvir")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* File Row */}
      <div
        className={cn(
          "flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl border border-gray-200 transition-all duration-300 mb-2.5",
          showFile ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
        )}
      >
        <div className="w-8 h-8 rounded-lg bg-[#1F6BFF]/10 flex items-center justify-center flex-shrink-0">
          <FileVideo className="w-4 h-4 text-[#1F6BFF]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-medium text-gray-700 truncate">
            video-apresentacao.mp4
          </p>
          <p className="text-[10px] text-gray-400">{translateInterfaceText("1.8 GB")}</p>
        </div>
        <span className="text-[11px] font-semibold text-[#1F6BFF] tabular-nums">
          {progress}%
        </span>
      </div>

      {/* Progress Bar */}
      <div
        className={cn(
          "space-y-1.5 transition-all duration-300",
          showProgress ? "opacity-100" : "opacity-0"
        )}
      >
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#1F6BFF] to-[#60A5FA] rounded-full transition-all duration-100 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-gray-500">
            {isComplete ? translateInterfaceText("Upload concluído") : translateInterfaceText("Enviando...")}
          </span>
          {isComplete && (
            <Check className="w-3.5 h-3.5 text-[#1F6BFF] animate-fade-in" />
          )}
        </div>
      </div>

      {/* Flexible spacer */}
      <div className="flex-1 min-h-[12px]" />

      {/* Fake Cursor */}
      {showCursor && (
        <svg
          className="absolute pointer-events-none z-50 drop-shadow-md"
          style={{
            left: cursorPos.x,
            top: cursorPos.y,
            transform: `scale(${cursorScale})`,
            transition: "left 700ms cubic-bezier(0.4, 0, 0.2, 1), top 700ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms ease-out",
          }}
          width="20"
          height="20"
          viewBox="0 0 24 24"
        >
          <path
            d="M5 3l14 9-8 2-3 8z"
            fill="white"
            stroke="#333"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}
